import React, { useMemo, useCallback } from 'react';
import { Flex, Spinner } from '@patternfly/react-core';
import { useTranslation } from '../../../../localization/useTranslation';
import ModelType from '../../../Model/ModelType';
import './DialPanel.scss';
import T from '../../../../localization/LocalizationKeys';
import Icons, { Size } from '../../../Common/Icons';
import _ from 'lodash';
import dataSources from '../../datasources';
import { SortDirection } from '../../../Common/SimpleTable/SimpleTable';
import { sortLocale } from '../../../../utils/string';
import DialPanelList from './DialPanelList';

import initialOptions from './intialOptions';
import fixedOptions from './fixedOptions';

const DEFAULT_HEIGHT = 150;
const DEFAULT_DECIMALS = 2;
const DEFAULT_FORMATTER = `<div style="text-align:center">
<span style="font-size:12px">{y}</span><br/>
<span style="font-size:12px;opacity:0.4">{{unit}}</span><br/>
<span style="font-size:12px;opacity:0.4">at {{timestamp}}</span>
</div>
`;

export const SELECTABLE_DATA_SOURCES = Object.freeze(_.map(_.pick(dataSources, ['realTimeTelemetry', 'lastValue']), ({ label }, type) => ({ type, label })));
// Here we select the default value for dataSource property
// TODO: use const for lastValue, or maybe use a default: true in the list of selectable data source.
export const DEFAULT_DATA_SOURCE = _.find(SELECTABLE_DATA_SOURCES, { type: 'realTimeTelemetry' });

const GROUP_BY = Object.freeze([
  {
    value: 'bySignal',
    text: T.common.groupBy.signal
  },
  {
    value: 'byAsset',
    text: T.common.groupBy.asset
  }
]);
const DEFAULT_GROUP_BY = _.find(GROUP_BY, { value: 'byAsset' });

const SORT_BY = Object.freeze([
  {
    value: SortDirection.ASC,
    text: T.common.sortBy.ascending
  },
  {
    value: SortDirection.DESC,
    text: T.common.sortBy.descending
  }
]);
const DEFAULT_SORT_BY = _.find(SORT_BY, { value: SortDirection.ASC });

const RealTimeDialPanelList = ({ data }) => {

  const { hasError, isLoading, signalData } = data.signalValues;
  const plotlines = useMemo(() => data.plotlineValues?.plotlines ?? [], [data.plotlineValues?.plotlines]);

  const T = useTranslation();
  const loadingDescription = hasError ? T.common.error.generic : T.common.loading;
  const options = useMemo(() => {
    return _.merge({}, data.options ?? initialOptions, fixedOptions, { time: { timezoneOffset: new Date().getTimezoneOffset() } });
  }, [data.options]);

  const renderDialList = useCallback(({ key, header, signalData, isLoading }) => {
    return (
      <DialPanelList
        key={key}
        header={header}
        signalData={signalData}
        isLoading={isLoading}
        dialOptions={options}
        gaugeHeight={data?.gaugeHeight ?? DEFAULT_HEIGHT}
        valueFormatter={data?.valueFormatter || DEFAULT_FORMATTER}
        plotlines={plotlines}
      />
    );
  }, [data?.gaugeHeight, data?.valueFormatter, options, plotlines]);

  const formatSignalData = useCallback((signalData, headerKey) => {
    const formatData = (values, decimals) => {
      return _.map(values, (value) => {
        const [timestamp, val] = value;
        const v = _.isNumber(val) ? Number(val.toFixed(decimals)) : val;
        return [timestamp, v];
      });

    };

    const formattedSignalData = _.map(signalData, (signal) => {
      return {
        ...signal,
        header: signal[headerKey],
        dataPoints: formatData(signal.dataPoints, data?.decimals || DEFAULT_DECIMALS)
      };
    });
    return formattedSignalData;
  }, [data?.decimals]);

  const sortItems = useCallback((items, key = 'signalDisplayName') => {
    const result = sortLocale(_.uniqBy(items, key), key);
    return (data?.sortBy === SortDirection.DESC) ? _.reverse(result) : result;
  }, [data?.sortBy]);

  const groupByAsset = useCallback((signalData, isLoading) => {
    const sortedAssets = sortItems(signalData, 'assetDisplayName');

    return (_.map(sortedAssets, (asset, index) => {
      const assetSignalData = _.filter(signalData, { assetId: asset.assetId });
      const formattedSignalData = formatSignalData(assetSignalData, 'signalDisplayName');
      return renderDialList({ key: index + '_' + asset.assetId, header: asset.assetDisplayName, signalData: sortItems(formattedSignalData), isLoading });
    }));
  }, [formatSignalData, renderDialList, sortItems]);

  const groupBySignal = useCallback((signalData, isLoading) => {
    const sortedSignals = sortItems(signalData, 'signalDisplayName');

    return (_.map(sortedSignals, (signal, index) => {
      const assetSignalData = _.filter(signalData, { signalId: signal.signalId });
      const formattedSignalData = formatSignalData(assetSignalData, 'assetDisplayName');
      return renderDialList({ key: index + '_' + signal.signalId, header: signal.signalDisplayName, signalData: sortItems(formattedSignalData, 'assetDisplayName'), isLoading });
    }, []));
  }, [formatSignalData, renderDialList, sortItems]);


  const renderGroupedTelemetry = useCallback(({ signalData, groupBy, isLoading }) => {
    if (_.isEmpty(signalData)) {
      return (
        <></>
      );
    }
    // console.log('renderGroupedTelemetry', groupBy);
    switch (groupBy) {
      case 'byAsset':
        return groupByAsset(signalData, isLoading);
      case 'bySignal':
        return groupBySignal(signalData, isLoading);
      default:
        return groupByAsset(signalData, isLoading);
    }
  }, [groupByAsset, groupBySignal]);


  return (
    <div >
      { (isLoading || hasError) && <Flex alignItems={{ default: 'alignItemsCenter' }}>
        <Spinner size="md" />
        <span>{ loadingDescription }</span>
      </Flex> }
      { renderGroupedTelemetry({ signalData, isLoading, groupBy: data?.groupBy }) }
    </div>
  );

};

RealTimeDialPanelList.models = [
  {
    path: 'sortBy',
    label: T.common.sortBy.label,
    type: ModelType.OPTIONSV2,
    options: SORT_BY,
    placeholder: DEFAULT_SORT_BY.text
  },
  {
    path: 'groupBy',
    label: T.common.groupBy.label,
    type: ModelType.OPTIONSV2,
    options: GROUP_BY,
    placeholder: DEFAULT_GROUP_BY.text
  },
  {
    type: ModelType.JSON_EDIT_DIALOG,
    path: 'options',
    label: T.dashboard.editor.inputs.json.options,
    properties: {
      height: '380px',
      width: '90%',
      initialValue: initialOptions
    }
  },
  {
    type: ModelType.NUMBER,
    path: 'gaugeHeight',
    label: 'height',
    placeholder: DEFAULT_HEIGHT
  },
  {
    type: ModelType.NUMBER,
    path: 'decimals',
    label: 'Decimals',
    placeholder: DEFAULT_DECIMALS
  },
  {
    type: ModelType.TEXTAREA,
    path: 'valueFormatter',
    label: 'Value Format',
    properties: {
      initialValue: DEFAULT_FORMATTER
    }
  }
];


RealTimeDialPanelList.info = {
  label: T.dashboard.panels.types.realtimeDialList,
  icon: <Icons.Dial size={Size.LARGE}/>
};

RealTimeDialPanelList.properties = panelProperties => {
  const modifiedProps = {
    signalValues: {
      type: panelProperties?.dataSource ?? DEFAULT_DATA_SOURCE.type
    },
    plotlineValues: {
      type: 'plotlines'
    }
  };
  return modifiedProps;
};


export default React.memo(RealTimeDialPanelList);
